import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-white/10 backdrop-blur-sm'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors ${
              isTransparent ? 'text-white hover:text-white/80' : 'text-foreground-900 hover:text-primary-600'
            }`}
          >
            <span className="text-lg font-bold font-heading whitespace-nowrap">
              VigorBuy <span className={isTransparent ? 'text-white' : 'text-primary-500'}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className={`text-sm transition-colors whitespace-nowrap ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              Home
            </Link>
            <a
              href="#categories"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              Categories
            </a>
            <Link
              to="/vigorbuy-buying-guide/"
              className={`text-sm transition-colors whitespace-nowrap ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              Shopping Guide
            </Link>
            <a
              href="#features"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              Features
            </a>
            <a
              href="#faq"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              FAQ
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              VigorBuy Sheet
              <i className="ri-arrow-right-up-line w-3.5 h-3.5 flex items-center justify-center"></i>
            </a>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
              isTransparent ? 'text-white' : 'text-foreground-700'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[75vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-2">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
              Home
            </Link>

            <button
              type="button"
              onClick={() => setCatOpen(!catOpen)}
              className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors cursor-pointer w-full"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                Categories
              </span>
              {catOpen ? (
                <i className="ri-arrow-up-s-line w-4 h-4 flex items-center justify-center"></i>
              ) : (
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              )}
            </button>

            {catOpen && (
              <div className="ml-4 pl-3 border-l-2 border-background-200/70 space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors"
                  >
                    <i className={`${cat.icon} w-3.5 h-3.5 flex items-center justify-center text-accent-500`}></i>
                    <span dangerouslySetInnerHTML={{ __html: cat.name }}></span>
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/vigorbuy-buying-guide/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors"
            >
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
              Shopping Guide
            </Link>

            <a
              href="#features"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
            >
              <i className="ri-star-line w-4 h-4 flex items-center justify-center"></i>
              Features
            </a>

            <a
              href="#faq"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
            >
              <i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>
              FAQ
            </a>

            <div className="pt-2 border-t border-background-200/70 mt-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer w-full justify-center"
              >
                VigorBuy Sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}